<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import ConversationApi from 'dashboard/api/inbox/conversation';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const accountId = computed(() => Number(route.params.accountId));
const stages = useMapGetter('kanbanStages/getStages');
const currentUser = useMapGetter('getCurrentUser');

const isAdmin = computed(
  () => currentUser.value?.role === 'administrator' ||
        currentUser.value?.type === 'SuperAdmin'
);

const loadingConversations = ref(true);
const conversations = ref([]);
const columns = reactive({});

const palette = [
  '#1f93ff', '#7c3aed', '#16a34a', '#f59e0b',
  '#ef4444', '#0ea5e9', '#8b5cf6', '#10b981',
];

const refreshColumns = () => {
  const grouped = { null: [] };
  stages.value.forEach(s => { grouped[s.id] = []; });
  conversations.value.forEach(c => {
    const key = c.kanban_stage_id ?? 'null';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });
  Object.keys(columns).forEach(k => { delete columns[k]; });
  Object.entries(grouped).forEach(([k, v]) => { columns[k] = v; });
};

watch([stages, conversations], refreshColumns, { deep: true });

const loadConversations = async () => {
  loadingConversations.value = true;
  try {
    const { data } = await ConversationApi.get({
      status: 'open',
      assigneeType: 'me',
      page: 1,
    });
    const allOpen = data?.data?.payload || [];
    // also fetch unassigned/assigned/all - fall back to "all" via direct call for completeness
    const allRes = await ConversationApi.get({
      status: 'open',
      assigneeType: 'assigned',
      page: 1,
    });
    const merged = [...allOpen, ...(allRes?.data?.data?.payload || [])];
    const dedup = Object.values(
      merged.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})
    );
    conversations.value = dedup;
  } finally {
    loadingConversations.value = false;
  }
};

const onColumnChange = async (stageKey, evt) => {
  const added = evt.added;
  if (!added) return;
  const conv = added.element;
  const targetStageId = stageKey === 'null' ? null : Number(stageKey);
  if (conv.kanban_stage_id === targetStageId) return;
  try {
    await ConversationApi.setKanbanStage({
      conversationId: conv.id,
      kanbanStageId: targetStageId,
    });
    conv.kanban_stage_id = targetStageId;
  } catch (e) {
    // revert: reload
    await loadConversations();
  }
};

const createStage = async () => {
  // eslint-disable-next-line no-alert
  const name = window.prompt(t('KANBAN.PROMPT.NEW_STAGE_NAME'));
  if (!name?.trim()) return;
  const color = palette[stages.value.length % palette.length];
  try {
    await store.dispatch('kanbanStages/create', { name: name.trim(), color });
  } catch (e) {
    // eslint-disable-next-line no-alert
    window.alert(t('KANBAN.ERRORS.CREATE_FAILED'));
  }
};

const renameStage = async stage => {
  // eslint-disable-next-line no-alert
  const name = window.prompt(t('KANBAN.PROMPT.RENAME_STAGE'), stage.name);
  if (!name?.trim() || name === stage.name) return;
  try {
    await store.dispatch('kanbanStages/update', { id: stage.id, name: name.trim() });
  } catch (e) {
    // eslint-disable-next-line no-alert
    window.alert(t('KANBAN.ERRORS.UPDATE_FAILED'));
  }
};

const deleteStage = async stage => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.PROMPT.DELETE_STAGE_CONFIRM', { name: stage.name }))) return;
  try {
    await store.dispatch('kanbanStages/delete', stage.id);
    await loadConversations();
  } catch (e) {
    // eslint-disable-next-line no-alert
    window.alert(t('KANBAN.ERRORS.DELETE_FAILED'));
  }
};

const openConversation = conv => {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conv.id },
  });
};

const conversationTitle = conv => {
  return conv.meta?.sender?.name || conv.additional_attributes?.contact?.name || `#${conv.id}`;
};

const conversationPreview = conv => {
  const lastMsg = conv.last_non_activity_message?.content;
  if (lastMsg) return lastMsg.slice(0, 90);
  return '';
};

const inboxName = conv => conv.meta?.channel || '';

onMounted(async () => {
  await store.dispatch('kanbanStages/get');
  await loadConversations();
  refreshColumns();
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1">
    <header class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0">
      <h1 class="text-lg font-semibold text-n-slate-12">
        {{ t('KANBAN.TITLE') }}
      </h1>
      <button
        v-if="isAdmin"
        type="button"
        class="px-3 py-1.5 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90"
        @click="createStage"
      >
        + {{ t('KANBAN.NEW_STAGE') }}
      </button>
    </header>

    <div
      v-if="loadingConversations"
      class="flex items-center justify-center flex-1"
    >
      <span class="text-sm text-n-slate-10">{{ t('KANBAN.LOADING') }}</span>
    </div>

    <div v-else class="flex flex-1 gap-4 px-4 py-4 overflow-x-auto">
      <!-- "no stage" column -->
      <section
        class="flex flex-col w-72 shrink-0 bg-n-solid-1 rounded-lg border border-n-weak"
      >
        <header class="flex items-center justify-between px-3 py-2 border-b border-n-weak">
          <div class="flex items-center gap-2">
            <span class="inline-block w-2 h-2 rounded-full bg-n-slate-9" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('KANBAN.UNSTAGED') }}
            </span>
            <span class="text-xs text-n-slate-10">
              ({{ (columns['null'] || []).length }})
            </span>
          </div>
        </header>
        <draggable
          :list="columns['null'] || []"
          group="kanban"
          item-key="id"
          class="flex flex-col gap-2 p-2 flex-1 min-h-[40px] overflow-y-auto"
          @change="evt => onColumnChange('null', evt)"
        >
          <template #item="{ element }">
            <article
              class="px-3 py-2 bg-n-solid-2 rounded-md border border-n-weak cursor-pointer hover:border-n-brand"
              @click="openConversation(element)"
            >
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ conversationTitle(element) }}
              </div>
              <div v-if="conversationPreview(element)" class="mt-1 text-xs text-n-slate-10 truncate">
                {{ conversationPreview(element) }}
              </div>
              <div class="flex items-center justify-between mt-2 text-xs text-n-slate-9">
                <span>#{{ element.id }}</span>
                <span class="capitalize">{{ inboxName(element) }}</span>
              </div>
            </article>
          </template>
        </draggable>
      </section>

      <section
        v-for="stage in stages"
        :key="stage.id"
        class="flex flex-col w-72 shrink-0 bg-n-solid-1 rounded-lg border border-n-weak"
      >
        <header class="flex items-center justify-between px-3 py-2 border-b border-n-weak">
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="inline-block w-2 h-2 rounded-full shrink-0"
              :style="{ backgroundColor: stage.color }"
            />
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ stage.name }}
            </span>
            <span class="text-xs text-n-slate-10 shrink-0">
              ({{ (columns[stage.id] || []).length }})
            </span>
          </div>
          <div v-if="isAdmin" class="flex items-center gap-1">
            <button
              type="button"
              class="text-xs text-n-slate-10 hover:text-n-slate-12"
              :title="t('KANBAN.RENAME')"
              @click="renameStage(stage)"
            >
              ✎
            </button>
            <button
              type="button"
              class="text-xs text-n-slate-10 hover:text-red-500"
              :title="t('KANBAN.DELETE')"
              @click="deleteStage(stage)"
            >
              ✕
            </button>
          </div>
        </header>
        <draggable
          :list="columns[stage.id] || []"
          group="kanban"
          item-key="id"
          class="flex flex-col gap-2 p-2 flex-1 min-h-[40px] overflow-y-auto"
          @change="evt => onColumnChange(String(stage.id), evt)"
        >
          <template #item="{ element }">
            <article
              class="px-3 py-2 bg-n-solid-2 rounded-md border border-n-weak cursor-pointer hover:border-n-brand"
              @click="openConversation(element)"
            >
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ conversationTitle(element) }}
              </div>
              <div v-if="conversationPreview(element)" class="mt-1 text-xs text-n-slate-10 truncate">
                {{ conversationPreview(element) }}
              </div>
              <div class="flex items-center justify-between mt-2 text-xs text-n-slate-9">
                <span>#{{ element.id }}</span>
                <span class="capitalize">{{ inboxName(element) }}</span>
              </div>
            </article>
          </template>
        </draggable>
      </section>

      <div v-if="!stages.length && isAdmin" class="flex items-center justify-center w-72 shrink-0">
        <button
          type="button"
          class="px-4 py-3 text-sm font-medium text-n-slate-11 border-2 border-dashed border-n-weak rounded-lg hover:border-n-brand hover:text-n-brand"
          @click="createStage"
        >
          + {{ t('KANBAN.CREATE_FIRST_STAGE') }}
        </button>
      </div>
    </div>
  </section>
</template>
